import { useObservable } from 'observable-hooks';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { useShallow } from 'zustand/react/shallow';

import { LatLng } from '@mopla/utils';

import { useGeoPositionContext } from './GeoPositionContext';
import { useGeolocationPermission } from './useGeolocationPermission';
import { useGeoPositionStore } from './useGeoPositionStore';

/**
 * Main public hook to access geolocation data and methods
 */
export const useGeoPosition = () => {
	/** Data from store */
	const stateData = useGeoPositionStore(
		useShallow((state) => ({
			coordinates: state.coordinates,
			address: state.address,
			permissionStatus: state.permissionStatus,
			isLoadingPosition: state.isLoadingPosition,
			isGeocoding: state.isGeocoding,
			isWatchingPosition: !!state.watchingPositionId,
			error: state.error,
		}))
	);

	/** Observable projection of coordinates from Zustand store */
	const position$ = useObservable(() =>
		new Observable<LatLng | null>((subscriber) => {
			subscriber.next(useGeoPositionStore.getState().coordinates);
			return useGeoPositionStore.subscribe((state) =>
				subscriber.next(state.coordinates)
			);
		}).pipe(shareReplay({ bufferSize: 1, refCount: true }))
	);

	/** Methods from context */
	const { fetchCurrentPosition, startWatchingPosition, stopWatchingPosition } =
		useGeoPositionContext();

	/** Permission handling */
	const { requestPermission, checkPermission } = useGeolocationPermission();

	return {
		...stateData,
		position$,
		requestPermission,
		checkPermission,
		fetchCurrentPosition,
		startWatchingPosition,
		stopWatchingPosition,
	};
};
